import { HeaderPage } from "@/components/HeaderPage";
import { CardContent } from "@/components/CardContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Eye, Trash2 } from "lucide-react";
import { Table } from "@/components/Table";
import type { User } from "@/types/User";
import { useDeleteUser, useGetUsers } from "@/hooks/useUsers";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { MdOutlineEdit } from "react-icons/md";
import { Loading } from "@/components/Loading";
import { useState, useEffect } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

const roleConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "outline" }
> = {
  ADMIN: { label: "Administrador", variant: "default" },
  AGENT: { label: "Agente", variant: "secondary" },
  CITIZEN: { label: "Cidadão", variant: "outline" },
};

export const Users = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [role, setRole] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const limit = 10;

  const queryClient = useQueryClient();
  const { data: users, isLoading } = useGetUsers({
    page: currentPage,
    limit,
    role: role !== "all" ? role : undefined,
    search: debouncedSearch || undefined,
  });
  const navigate = useNavigate();
  const deleteUser = useDeleteUser();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRoleChange = (value: string) => {
    setRole(value);
    setCurrentPage(1);
  };

  const handleDeleteClick = (id: string) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser.mutateAsync(userToDelete);
      toast.success("Usuário deletado com sucesso!");

      queryClient.invalidateQueries({ queryKey: ["getUsers"] });

      setUserToDelete(null);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Erro ao deletar usuário";
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <HeaderPage
        title="Usuários"
        description="Gerencie os usuários do sistema"
      />

      <Card className="mb-6 mt-6 bg-white">
        <CardContentUI className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar usuários..."
                className="pl-10 h-10 text-sm"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <Select value={role} onValueChange={handleRoleChange}>
                <SelectTrigger className="w-45 h-10 text-sm">
                  <SelectValue placeholder="Função" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="ADMIN">Administrador</SelectItem>
                  <SelectItem value="AGENT">Agente</SelectItem>
                  <SelectItem value="CITIZEN">Cidadão</SelectItem>
                </SelectContent>
              </Select>
              <Button
                className="h-10 text-sm px-4 text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                onClick={() => navigate("/users/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Usuário
              </Button>
            </div>
          </div>
        </CardContentUI>
      </Card>

      <CardContent
        title="Lista de Usuários"
        content={
          isLoading ? (
            <Loading label="Carregando usuários.." />
          ) : (
            <Table<User>
              columns={[
                { key: "id", label: "Id" },
                { key: "email", label: "Email" },
                {
                  key: "profile",
                  label: "Nome Completo",
                  render: (item) => item.profile?.fullName || "-",
                },
                {
                  key: "profile",
                  label: "CPF",
                  render: (item) => {
                    const cpf = item.profile?.cpf;
                    if (!cpf) return "-";
                    return cpf.replace(
                      /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
                      "$1.$2.$3-$4",
                    );
                  },
                },
                {
                  key: "profile",
                  label: "Telefone",
                  render: (item) => {
                    const phone = item.profile?.phone;
                    if (!phone) return "-";
                    return phone.replace(
                      /^(\d{2})(\d{5})(\d{4})$/,
                      "($1) $2-$3",
                    );
                  },
                },
                {
                  key: "role",
                  label: "Função",
                  render: (item) => {
                    const config = roleConfig[item.role] || {
                      label: item.role,
                      variant: "outline" as const,
                    };
                    return (
                      <Badge variant={config.variant}>{config.label}</Badge>
                    );
                  },
                },
                {
                  key: "isActive",
                  label: "Status",
                  render: (item) => (
                    <Badge variant={item.isActive ? "success" : "destructive"}>
                      {item.isActive ? "Ativo" : "Inativo"}
                    </Badge>
                  ),
                },
                {
                  key: "actions",
                  label: "Ações",
                  render: (item) => {
                    return (
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Visualizar"
                          onClick={() => navigate(`/users/view/${item.id}`)}
                        >
                          <Eye className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Editar"
                          onClick={() => navigate(`/users/edit/${item.id}`)}
                        >
                          <MdOutlineEdit className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-red-50 transition-colors"
                          title="Deletar"
                          onClick={() => handleDeleteClick(item.id)}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
              data={users?.data || []}
              pagination={users?.pagination}
              onPageChange={handlePageChange}
            />
          )
        }
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Confirmar Deleção"
        description="Tem certeza que deseja deletar este usuário? Esta ação não pode ser desfeita."
        confirmText="Deletar"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteUser.isPending}
      />
    </>
  );
};
